/**
 * Archive Project Dialog Component
 * @implements FS-002.5 - Project Lifecycle States
 * @implements BS-002.9 - Project Archival
 * @implements US-016 - Project Archival
 * @implements TS-065 - Archive/Unarchive UI
 *
 * Confirmation dialog shown before archiving a project.
 * Explains consequences: project becomes read-only, can be unarchived later.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Lock as LockIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';

/**
 * Props for ArchiveProjectDialog component
 */
export interface ArchiveProjectDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** The name of the project being archived (for display) */
  projectTitle: string;
  /** Callback when the dialog is closed (cancel) */
  onClose: () => void;
  /** Callback when the user confirms archiving */
  onConfirm: () => void;
  /** Whether the archive action is in progress */
  isLoading?: boolean;
}

/**
 * Archive Project Dialog Component
 * @implements TS-065 - Archive/Unarchive UI
 *
 * Displays a confirmation dialog before archiving a project.
 * Explains the consequences of archiving:
 * - Project becomes read-only
 * - No editing or state changes allowed
 * - Project can be unarchived later to restore editability
 *
 * @example
 * ```tsx
 * <ArchiveProjectDialog
 *   open={dialogOpen}
 *   projectTitle={project.title}
 *   onClose={() => setDialogOpen(false)}
 *   onConfirm={handleArchive}
 *   isLoading={isArchiving}
 * />
 * ```
 */
export const ArchiveProjectDialog = ({
  open,
  projectTitle,
  onClose,
  onConfirm,
  isLoading = false,
}: ArchiveProjectDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      aria-labelledby="archive-project-dialog-title"
      aria-describedby="archive-project-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        id="archive-project-dialog-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <ArchiveIcon color="action" />
        Archive Project?
      </DialogTitle>

      <DialogContent>
        <DialogContentText
          id="archive-project-dialog-description"
          component="div"
        >
          Are you sure you want to archive <strong>{projectTitle}</strong>?
          <List dense sx={{ mt: 2 }}>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LockIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Project becomes read-only"
                secondary="No editing or state changes will be allowed"
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <RestoreIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText
                primary="Can be unarchived later"
                secondary="You can restore full editability at any time"
              />
            </ListItem>
          </List>
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          color="primary"
          variant="contained"
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <ArchiveIcon />
            )
          }
        >
          {isLoading ? 'Archiving...' : 'Archive Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
